import { useState, useEffect, useRef } from 'react';
import { getAllCategories, searchProducts, getAllStores } from '@/api';

export interface SearchPlaceholderItem {
  text: string;
}

export function useSearchPlaceholder() {
  const [currentItem, setCurrentItem] = useState<SearchPlaceholderItem | null>(null);
  const [isReady, setIsReady] = useState(false);
  const poolRef = useRef<SearchPlaceholderItem[]>([]);
  const lastIdxRef = useRef(-1);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function buildPool() {
      try {
        const [catRes, prodRes, storeRes] = await Promise.all([
          getAllCategories().catch(() => []),
          searchProducts('', 50).catch(() => ({ products: [] })),
          getAllStores(1, 20).catch(() => []),
        ]);

        if (cancelled) return;

        const items: SearchPlaceholderItem[] = [];

        if (Array.isArray(catRes)) {
          catRes.forEach((c: { name: string }) => {
            items.push({ text: c.name });
          });
        }

        const prodList = Array.isArray(prodRes) ? prodRes : (prodRes as any)?.products || [];
        prodList.forEach((p: { name: string }) => {
          items.push({ text: p.name });
        });

        const storeList = Array.isArray(storeRes) ? storeRes : (storeRes as any)?.stores || [];
        if (storeList.length > 0) {
          // Try to prioritize stores near user's saved address
          const savedCityRaw = localStorage.getItem('selectedDeliveryAddress') || '';
          const savedCity = savedCityRaw.split(',')[1]?.trim().toLowerCase() || '';

          const nearby: SearchPlaceholderItem[] = [];
          const others: SearchPlaceholderItem[] = [];

          storeList.forEach((s: { name: string; address: string | null }) => {
            const location = s.name || s.address || '';
            if (!location) return;
            const item: SearchPlaceholderItem = { text: location };
            if (savedCity && s.address && s.address.toLowerCase().includes(savedCity)) {
              nearby.push(item);
            } else {
              others.push(item);
            }
          });

          const storeItems = [...nearby, ...others];
          for (let i = storeItems.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [storeItems[i], storeItems[j]] = [storeItems[j], storeItems[i]];
          }
          items.push(...storeItems);
        }

        // Shuffle the whole pool
        for (let i = items.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [items[i], items[j]] = [items[j], items[i]];
        }

        // Avoid showing the same first item if we already had one
        if (items.length > 1 && currentItem) {
          const firstIdx = items.findIndex(it => it.text === currentItem.text);
          if (firstIdx === 0) {
            // Swap with last item
            [items[0], items[items.length - 1]] = [items[items.length - 1], items[0]];
          }
        }

        poolRef.current = items;
        if (items.length > 0) {
          setCurrentItem(items[0]);
          lastIdxRef.current = 0;
        }
        setIsReady(true);
      } catch (err) {
        console.error('[SearchPlaceholder] Failed to load data:', err);
        setIsReady(true);
      }
    }

    buildPool();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!isReady || poolRef.current.length <= 1) return;

    intervalRef.current = setInterval(() => {
      const pool = poolRef.current;
      let nextIdx = Math.floor(Math.random() * pool.length);
      if (nextIdx === lastIdxRef.current && pool.length > 1) {
        nextIdx = (nextIdx + 1) % pool.length;
      }
      lastIdxRef.current = nextIdx;
      setCurrentItem(pool[nextIdx]);
    }, 3000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isReady]);

  return { currentItem, isReady };
}